// src/components/Auth/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isSupabaseConfigured } from '../../config/supabase';
import { capabilities } from '../../platform';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import PasswordStrength from './PasswordStrength';
import './Login.css';

export default function Login({ initialMode = 'signin' }) {
  const [mode, setMode] = useState(initialMode); // signin, signup, reset
  useDocumentTitle(mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Sign Up' : 'Reset Password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const {
    signIn,
    signUp,
    signInWithGoogle,
    signInWithApple,
    resetPassword,
    error,
    clearError,
    user
  } = useAuth();
  
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || '/home';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  // Clear errors when mode changes
  useEffect(() => {
    clearError();
    setMessage(null);
  }, [mode, clearError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    clearError();

    try {
      if (mode === 'signin') {
        await signIn(email, password);
        // Navigation handled by useEffect when user state changes
      } else if (mode === 'signup') {
        const result = await signUp(email, password, { name });
        if (result.session) {
          // User is logged in immediately
          // Navigation handled by useEffect
        } else {
          // Email confirmation required — redirect to verification page
          sessionStorage.setItem('pendingVerificationEmail', email);
          navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        }
      } else if (mode === 'reset') {
        await resetPassword(email);
        setMessage('Check your email for the password reset link.');
        setMode('signin');
      }
    } catch (err) {
      // Error is already set in context
    } finally {
      setLoading(false);
    }
  };

  // On web this navigates away and the component unmounts mid-flight; on
  // native it resolves in place, so the loading state has to be released.
  const handleOAuthLogin = (start) => async () => {
    setLoading(true);
    setMessage(null);
    clearError();

    try {
      // Clear stale PKCE keys before initiating OAuth
      const pkceKeys = Object.keys(localStorage).filter(key =>
        key.includes('supabase.auth.pkce')
      );
      if (pkceKeys.length > 0) {
        pkceKeys.forEach(key => localStorage.removeItem(key));
      }

      await start();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      // Dismissing the provider sheet isn't a failure — don't show it in red.
      if (/cancel/i.test(err?.message || '')) clearError();
    }
  };

  const handleGoogleLogin = handleOAuthLogin(signInWithGoogle);
  const handleAppleLogin = handleOAuthLogin(signInWithApple);

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1 className="login-title">TekStak</h1>
          <p className="login-subtitle">
            {mode === 'signin' ? 'Welcome back' : mode === 'signup' ? 'Create your account' : 'Reset your password'}
          </p>
        </div>

        {!isSupabaseConfigured && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <p className="text-yellow-400 text-sm font-medium mb-1">Authentication not configured</p>
            <p className="text-yellow-300 text-sm">
              Set <code>REACT_APP_SUPABASE_URL</code> and <code>REACT_APP_SUPABASE_ANON_KEY</code> in your <code>.env</code>, then restart the dev server.
            </p>
          </div>
        )}

        {message && (
          <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-blue-400 text-sm">{message}</p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <input
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="login-input"
            />
          )}
          
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="login-input"
            disabled={loading}
          />
          
          {mode !== 'reset' && (
            <>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="login-input"
                disabled={loading}
                minLength={mode === 'signup' ? 6 : undefined}
              />
              {mode === 'signup' && <PasswordStrength password={password} />}
            </>
          )}
          
          <button
            type="submit"
            disabled={loading || !isSupabaseConfigured}
            className="login-button"
          >
            {loading ? 'Loading...' : (
              mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Send Reset Email'
            )}
          </button>
        </form>

        {/* App Review Guideline 4.8: an app offering Google login must also
            offer Sign in with Apple. These two ship and retire together. */}
        {mode !== 'reset' && capabilities.oauthButtons && (
          <>
            <div className="oauth-divider">Or continue with</div>

            <button
              onClick={handleAppleLogin}
              disabled={loading || !isSupabaseConfigured}
              title={!isSupabaseConfigured ? 'Configure Supabase in .env to enable Apple sign-in' : undefined}
              className="login-button oauth-button oauth-button-apple"
              type="button"
            >
              <svg className="oauth-logo" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M17.05 12.54c-.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.61-1.7-3.18-1.72-1.35-.14-2.64.79-3.33.79-.69 0-1.75-.77-2.87-.75-1.48.02-2.84.86-3.6 2.18-1.53 2.66-.39 6.6 1.11 8.76.73 1.06 1.6 2.25 2.75 2.2 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.71.71 2.87.69 1.19-.02 1.94-1.08 2.66-2.14.84-1.23 1.19-2.42 1.21-2.48-.03-.01-2.32-.89-2.35-3.51zM14.9 5.9c.61-.74 1.02-1.77.91-2.8-.88.04-1.94.59-2.57 1.32-.56.65-1.05 1.7-.92 2.7.98.08 1.98-.5 2.58-1.22z"/>
              </svg>
              Sign in with Apple
            </button>

            <button
              onClick={handleGoogleLogin}
              disabled={loading || !isSupabaseConfigured}
              title={!isSupabaseConfigured ? 'Configure Supabase in .env to enable Google sign-in' : undefined}
              className="login-button oauth-button"
              type="button"
            >
              <svg className="oauth-logo" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>

          </>
        )}

        <div className="switcher text-center text-sm">
          {mode === 'signin' ? (
            <>
              <button onClick={() => setMode('reset')} className="link-button">Forgot password?</button>
              <p className="mt-2">
                Don't have an account?{' '}
                <button onClick={() => { setMode('signup'); navigate('/signup', { replace: true }); }} className="link-button">Sign up</button>
              </p>
            </>
          ) : mode === 'signup' ? (
            <p>
              Already have an account?{' '}
              <button onClick={() => { setMode('signin'); navigate('/login', { replace: true }); }} className="link-button">Sign in</button>
            </p>
          ) : (
            <button onClick={() => { setMode('signin'); navigate('/login', { replace: true }); }} className="link-button">Back to sign in</button>
          )}
        </div>

        <p className="login-footer">
          By signing in, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}